<template>
  <div class="tir-pm-context-menu">
    <div
      class="tir-pm-context-menu__target"
      ref="$target"
      @mousedown.middle="ClickTarget"
      @click="ClickTarget"
      @contextmenu="ClickTarget"
    >
      <slot />
    </div>
    <teleport :to="teleportEl">
      <div
        class="tir-pm-context-menu__content"
        ref="$content"
        :style="contentStyle"
        v-if="isVisibleContent"
      >
        <slot name="content" />
      </div>
    </teleport>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  CSSProperties,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
} from "vue";

import { debounce } from "tir-pm-debounce";
import { RelativeElInfo, Position, PositionOptions } from "./types";
import { TirPmContextMenuMouseButtonEnum } from "./enums";
import { useRootFontSize } from "tir-pm-toolkit";

const props = defineProps<{
  /** Элемент относительно которого выполняется позиционирование */
  relativeEl?: HTMLElement;
  /** Родительский элемент со скролом */
  scrollEl?: HTMLElement;
  /** Кнопки на которые реагирует контекстное меню */
  triggerButtons?: Array<TirPmContextMenuMouseButtonEnum>;
  /** Минимальная ширина */
  minWidth?: string;
  /** Максимальная ширина */
  maxWidth?: string;
  /** Минимальная высота */
  minHeight?: string;
  /** Максимальная высота */
  maxHeight?: string;
  /** Флаг режима позиционирования относительно relative элемента */
  isModeRelative?: boolean;
  /** Отключить контекстное меню */
  isDisabled?: boolean;
}>();

const { rootFontSizePx } = useRootFontSize();

/** Ссылка на таргет элемент */
const $target = ref<HTMLElement>();
/** Ссылка на элемент с контентом */
const $content = ref<HTMLElement>();

/** Элемент относительно которого выполняется позиционирование */
const relativeEl = computed(
  () =>
    (props.isModeRelative ? props.relativeEl : document.documentElement) ||
    document.documentElement
);
/** Элемент для телепорта */
const teleportEl = computed(
  () =>
    (props.isModeRelative ? props.relativeEl : document.body) || document.body
);
/** Родительский элемент скролла */
const scrollEl = computed(
  () =>
    (props.isModeRelative ? props.relativeEl : document.documentElement) ||
    document.documentElement
);

const triggerButton = computed(
  () => props.triggerButtons || [TirPmContextMenuMouseButtonEnum.Right]
);

/** Позиция клика (для сохранения) */
const clickTargetPosition = ref<Position>();

/** Позиция контекстого меню */
const position = ref<Position>({
  x: 0,
  y: 0,
});

/** Уставливаем позиции контекстого меню */
const SetPosition = (value: Position) => (position.value = value);

/** Виден ли контент */
const isVisibleContent = ref(false);

/** Получаем DOMRect для таргет элемента */
const GetTargetRect = () => $target.value?.getBoundingClientRect();
/** Получаем DOMRect для контент элемента */
const GetContentRect = () => $content.value?.getBoundingClientRect();

/**
 * Получаем размеры релектив элемента
 */
const GetRelativeElInfo = (): RelativeElInfo | undefined => {
  if (relativeEl.value) {
    return {
      width: relativeEl.value.clientWidth,
      height: relativeEl.value.clientHeight,
      scrollY: scrollEl.value.scrollTop,
      scrollX: scrollEl.value.scrollLeft,
    };
  }
};

/**
 * Конвертируем координаты отсносительно всей страницы в координаты относительно определенноо элемента
 * @param position Координаты
 * @param relativeEl Элемент относительно которого конвертируем
 */
const ConvertAbsolutePosition_ToReletiveElPosition = (
  position: Position,
  relativeEl: HTMLElement
): Position => {
  const rect = relativeEl.getBoundingClientRect();
  return {
    x: position.x - rect.x,
    y: position.y - rect.y,
  };
};

const GetPositionOptions = (
  x: number,
  y: number
): PositionOptions | undefined => {
  // Получаем размеры окна браузера
  const relativeElInfo = GetRelativeElInfo();
  // DOMRect таргет элемента
  const targetRect = GetTargetRect();
  // DOMRect контент элемента
  const contentRect = GetContentRect();

  if (targetRect && contentRect && relativeElInfo) {
    return {
      relativeElWidth: relativeElInfo.width,
      relativeElHeight: relativeElInfo.height,
      scrollX: relativeElInfo.scrollX,
      scrollY: relativeElInfo.scrollY,
      x,
      y,
      targetRect,
      contentRect,
    };
  }
};

/**
 * Вычисляем позицию слева
 * @param options Данные для вычисления
 */
const CalcLeftPosition = (options: PositionOptions): number => {
  // Минимальная позиция слева (начало экрана)
  const minLeft = 0;
  // Максимальная позиция слева (конец экрана)
  const maxLeft = options.relativeElWidth - options.contentRect.width;

  return Math.min(Math.max(options.x, minLeft), maxLeft);
};

/**
 * Вычисляем позицию сверху
 * @param options Данные для вычисления
 */
const CalcTopPosition = (options: PositionOptions): number => {
  // Минимальная позиция сверху (начало экрана)
  const minTop = 0 - options.scrollY;
  // Максимальная позиция сверху (конец экрана)
  const maxTop = options.relativeElHeight - options.contentRect.height;

  return Math.min(Math.max(options.y - options.scrollY, minTop), maxTop);
};

/**
 * Просчитываем позицию контекстного меню
 * @param options Данные для просчета позиции
 */
const CalcPosition = (options: PositionOptions): Position => {
  return {
    x: CalcLeftPosition(options),
    y: CalcTopPosition(options),
  };
};

/**
 * Обновляем позицию
 * @param options Данные для просчета позиции
 */
const UpdatePosition = (options: PositionOptions) => {
  const position = CalcPosition(options);
  SetPosition(position);
};

const styleSize = computed<CSSProperties>(() => {
  return {
    minWidth: props.minWidth ?? "5rem",
    maxWidth: props.maxWidth ?? "18.75rem",
    minHeight: props.minHeight,
    maxHeight: props.maxHeight ?? "12.5rem",
  };
});

/** Стили для элемета с контентом */
const contentStyle = computed<CSSProperties>(() => {
  return {
    left: position.value.x / rootFontSizePx.value + "rem",
    top: position.value.y / rootFontSizePx.value + "rem",
    ...styleSize.value,
  };
});

/**
 * Пересчитываем позицию на основании ранее сохраненной позиции клика
 */
const RecalculationPosition = () => {
  if (props.isDisabled || !isVisibleContent.value || !clickTargetPosition.value)
    return;

  // Получаем опции по ранее сохраненной позиции клика
  const positionOptions = GetPositionOptions(
    clickTargetPosition.value?.x,
    clickTargetPosition.value?.y
  );

  // Обновляем позицию
  if (positionOptions) UpdatePosition(positionOptions);
};

const debounceRecalculationPosition = debounce(RecalculationPosition, 12);

/** Resize Observer на контент */
const observeResizeEl = new ResizeObserver(debounceRecalculationPosition);

/** Запускаем resize observer */
const StartObserve = () => {
  if ($content.value) observeResizeEl.observe($content.value);
};
/** Отключаем resize observer */
const DisconnectObserve = () => observeResizeEl.disconnect();

/** Открыть контекстное меню */
const Open = async () => {
  // Отображаем контент
  isVisibleContent.value = true;
  // Ждем рендера
  await nextTick();
  // Запускаем обсервер
  StartObserve();
};
/** Закрыть котекстное меню */
const Close = () => {
  isVisibleContent.value = false;
  DisconnectObserve();
};

/**
 * * Обрабатываем клик на таргет элемент
 * @param e
 */
const ClickTarget = async (event: MouseEvent) => {
  if (props.isDisabled) return;
  if (!triggerButton.value.includes(event.button)) return;
  event.preventDefault();
  // Открываем меню
  Open();
  // Ждем отрисовки контента
  await nextTick();

  // Получаем координаты клика
  const { clientX, clientY } = event;

  const position = ConvertAbsolutePosition_ToReletiveElPosition(
    { x: clientX, y: clientY },
    relativeEl.value
  );

  // Запоминаем координаты
  clickTargetPosition.value = {
    x: position.x,
    y: position.y,
  };

  // Получаем опции для просчета позиции
  const positionOptions = GetPositionOptions(position.x, position.y);

  // Обновляем поизицию
  if (positionOptions) UpdatePosition(positionOptions);
};

/**
 * Если есть relative элемент добавляем ему position relative
 */
const RelativeElementSetStyle = async () => {
  await nextTick();

  if (props.relativeEl) {
    const computedStyle = window.getComputedStyle(props.relativeEl);
    props.relativeEl.style.position = "relative";
    if (computedStyle.transform === "none")
      props.relativeEl.style.transform = "translateZ(0)";
  }
};

/**
 * Обрабатываем clickOutside
 * @param e
 */
const ClickOutside = (e: MouseEvent) => {
  const target = e.target as HTMLElement;

  if (!$target.value?.contains(target) && !$content.value?.contains(target))
    Close();
};

onMounted(() => {
  window.addEventListener("mousedown", ClickOutside);
  window.addEventListener("resize", debounceRecalculationPosition);
  RelativeElementSetStyle();

  window.addEventListener("scroll", debounceRecalculationPosition);
});

onUnmounted(() => {
  window.removeEventListener("mousedown", ClickOutside);
  window.removeEventListener("resize", debounceRecalculationPosition);
  window.removeEventListener("scroll", debounceRecalculationPosition);
  DisconnectObserve();
});

defineExpose({
  setPosition: SetPosition,
  $target,
  $content,
  open: Open,
  close: Close,
  isVisible: isVisibleContent,
});
</script>

<style lang="scss" scoped>
@import "./tir-pm-context-menu.scss";
</style>
